import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dimensions,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from "react-native";
import { AntDesign, MaterialCommunityIcons } from "@expo/vector-icons";
import { useDispatch, useSelector } from "react-redux";
import { getMergedTrackerOrders } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { Text, View } from "../../../components/shared/Themed";
import TrackerEnquiryModal from "./TrackerEnquiryModal";
import TrackerHistoryModal from "./TrackerHistoryModal";
import Spinner from "../../../components/shared/Spinner";
import { formatLocalDate } from "../../../utils/common";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import ChinaTrackerFlagModal from "./ChinaTrackerFlagModal";
import ChinaShipmentsListModal from "./ChinaShipmentsListModal";

// ─── Props ────────────────────────────────────────────────────────────────────
interface ChinaTrackerDetailsReportModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

// ─── Component ────────────────────────────────────────────────────────────────
const ChinaTrackerDetailsReportModal = ({
  visible,
  onClose,
  order,
}: ChinaTrackerDetailsReportModalProps) => {
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [progressBar, setProgressBar] = useState<number>(0);
  const [trackerAction, setTrackerAction] = useState<string | null>(null);
  const [subModalOpen, setSubModalOpen] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width,
  );

  const { loading, filteredOrders, orders, isProductTrackerLoaded } =
    useSelector((state: any) => state.chinaTracker);
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();

  const closeSubModal = () => setSubModalOpen(false);

  // ── Fetch orders if not yet loaded ────────────────────────────────────────
  const fetchTrackerOrders = useCallback(() => {
    dispatch(getMergedTrackerOrders({ refresh: false }));
  }, [dispatch]);

  useEffect(() => {
    if ((!orders || orders.length === 0) && !isProductTrackerLoaded) {
      fetchTrackerOrders();
    }
  }, [orders, isProductTrackerLoaded, fetchTrackerOrders]);

  // ── Resolve the full order object ─────────────────────────────────────────
  useEffect(() => {
    if (!order || typeof order === "string") return;

    let orderToUse: any = order;

    if (!order.details && (order.key || order.Key)) {
      if (!filteredOrders || filteredOrders.length === 0) return;

      const orderKey = order.key || order.Key;
      const retrieved = filteredOrders.find(
        (o: any) => o.Key === orderKey || o.key === orderKey,
      );

      if (retrieved) {
        orderToUse = retrieved;
      } else {
        console.warn(`Order with key ${orderKey} not found in filteredOrders`);
        return;
      }
    }

    setSelectedOrder((prev: any) => {
      const prevKey = prev?.Key || prev?.key;
      const newKey = orderToUse.Key || orderToUse.key;
      return !prev || prevKey !== newKey ? orderToUse : prev;
    });
  }, [order, filteredOrders]);

  // ── Sync progress bar with fulfillment ────────────────────────────────────
  useEffect(() => {
    if (selectedOrder?.FulfillmentPercent != null) {
      setProgressBar(selectedOrder.FulfillmentPercent);
    }
  }, [selectedOrder]);

  // ── Track window width ────────────────────────────────────────────────────
  useEffect(() => {
    const sub = Dimensions.addEventListener("change", ({ window }) =>
      setWindowWidth(window.width),
    );
    return () => sub?.remove();
  }, []);

  const isMobileView = windowWidth < 768;

  // ── Header ────────────────────────────────────────────────────────────────
  const ModalHeader = () => (
    <View style={styles.drawerHeaderStyle}>
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        {/* Close / back button */}
        <TouchableOpacity
          style={{ paddingHorizontal: 15, paddingVertical: 5 }}
          onPress={onClose}
        >
          <AntDesign name="arrowleft" size={24} color="black" />
        </TouchableOpacity>

        <View
          style={{
            flex: 1,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: 15,
          }}
        >
          <Text selectable style={{ fontWeight: "700", fontSize: 14.5 }}>
            {`${translateFields(
              "chinaordernumber",
              t,
              chinaTrackerTranslationMap,
            )}: ${selectedOrder?.OrderNumber1 || "AB130442"}`}
          </Text>

          {/* Action icons */}
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {[
              { action: "history", icon: "clock-outline" },
              { action: "shipments", icon: "truck-outline" },
            ].map(({ action, icon }) => (
              <TouchableOpacity
                key={action}
                onPress={() => {
                  setTrackerAction(action);
                  setSubModalOpen(true);
                }}
              >
                <MaterialCommunityIcons
                  name={icon as any}
                  size={20}
                  color="#5F6368"
                  style={styles.actionsIcons}
                />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </View>
  );

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      {/* Overlay */}
      <View style={styles.overlay}>
        {/* Modal card */}
        <View style={styles.card}>
          <ModalHeader />

          {loading ? (
            <View style={styles.spinnerContainer}>
              <Spinner size="small" color="#74BBFF" />
            </View>
          ) : (
            <ScrollView contentContainerStyle={styles.scrollContent}>
              <View
                style={[
                  styles.contentContainer,
                  isMobileView && styles.contentContainerMobile,
                ]}
              >
                {/* ── Left: Order Details ─────────────────────────────── */}
                <View
                  style={[
                    styles.orderDetails,
                    isMobileView && styles.orderDetailsMobile,
                  ]}
                >
                  {/* Status & Priority */}
                  <View
                    style={[
                      styles.detailBox,
                      isMobileView && styles.detailBoxMobile,
                    ]}
                  >
                    <View style={styles.detailRow}>
                      <Text selectable style={styles.labelText}>
                        {translateFields(
                          "OrderStatus",
                          t,
                          chinaTrackerTranslationMap,
                        )}
                      </Text>
                      <View
                        style={[
                          styles.statusContainer,
                          { backgroundColor: "#f2f2f2" },
                        ]}
                      >
                        {selectedOrder?.OrderStatus === "On Time" && (
                          <AntDesign name="check" size={16} color="green" />
                        )}
                        <Text
                          selectable
                          style={{
                            color: getStatusColor(selectedOrder?.OrderStatus),
                            marginLeft: 5,
                            fontWeight: "600",
                          }}
                        >
                          {selectedOrder?.OrderStatus === "At Risk"
                            ? t("atRisk")
                            : selectedOrder?.OrderStatus === "On Time"
                              ? t("onTime")
                              : selectedOrder?.OrderStatus === "Delayed"
                                ? t("delayed")
                                : selectedOrder?.OrderStatus === "Planned"
                                  ? t("planned")
                                  : selectedOrder?.OrderStatus === "No Data"
                                    ? t("noData")
                                    : selectedOrder?.OrderStatus ===
                                        "Interrupted"
                                      ? t("interrupted")
                                      : selectedOrder?.OrderStatus ===
                                          "Cancelled"
                                        ? t("cancelled")
                                        : selectedOrder?.OrderStatus ===
                                            "Completed"
                                          ? t("cancelled")
                                          : t("closed")}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.detailRow}>
                      <Text selectable style={styles.labelText}>
                        {translateFields(
                          "PriorityOrder",
                          t,
                          chinaTrackerTranslationMap,
                        )}
                      </Text>
                      <View
                        style={[
                          styles.statusContainer,
                          { backgroundColor: "#f2f2f2" },
                        ]}
                      >
                        <Text
                          selectable
                          style={{
                            color: getStatusColor(selectedOrder?.PriorityOrder),
                            marginLeft: 5,
                            fontWeight: "600",
                          }}
                        >
                          {selectedOrder?.PriorityOrder === "High"
                            ? t("high")
                            : selectedOrder?.PriorityOrder === "Medium"
                              ? t("medium")
                              : selectedOrder?.PriorityOrder === "Low"
                                ? t("low")
                                : t("normal")}
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* Order Numbers */}
                  <View
                    style={[
                      styles.detailBox,
                      isMobileView && styles.detailBoxMobile,
                    ]}
                  >
                    {[
                      {
                        key: "OrderNumber1",
                        value: selectedOrder?.OrderNumber1,
                      },
                      { key: "MSRef", value: selectedOrder?.MSRef },
                      {
                        key: "allocateProductionLine",
                        value: selectedOrder?.allocateProductionLine,
                      },
                      {
                        key: "StateDelivery",
                        value: selectedOrder?.StateDelivery,
                      },
                      { key: "OrderDate", value: selectedOrder?.OrderDate },
                    ].map(({ key, value }) => (
                      <View key={key} style={styles.detailRow}>
                        <Text selectable style={styles.labelText}>
                          {translateFields(key, t, chinaTrackerTranslationMap)}
                        </Text>
                        <Text selectable style={styles.valueText}>
                          {value}
                        </Text>
                      </View>
                    ))}
                  </View>

                  {/* Item Info */}
                  <View
                    style={[
                      styles.detailBox,
                      isMobileView && styles.detailBoxMobile,
                    ]}
                  >
                    {[
                      { key: "ItemCode", value: selectedOrder?.ItemCode || "" },
                      { key: "ItemName", value: selectedOrder?.ItemName },
                    ].map(({ key, value }) => (
                      <View key={key} style={styles.detailRow}>
                        <Text selectable style={styles.labelText}>
                          {translateFields(key, t, chinaTrackerTranslationMap)}
                        </Text>
                        <Text selectable style={styles.valueText}>
                          {value}
                        </Text>
                      </View>
                    ))}
                  </View>

                  {/* Quantity & Fulfillment */}
                  <View
                    style={[
                      styles.detailBox,
                      isMobileView && styles.detailBoxMobile,
                    ]}
                  >
                    <View style={styles.detailRow}>
                      <Text selectable style={styles.labelText}>
                        {translateFields(
                          "ArtworkNo",
                          t,
                          chinaTrackerTranslationMap,
                        )}
                      </Text>
                      <Text selectable style={styles.valueText}>
                        {selectedOrder?.ArtworkNo || ""}
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable style={styles.labelText}>
                        {translateFields(
                          "RequestDate",
                          t,
                          chinaTrackerTranslationMap,
                        )}
                      </Text>
                      <Text selectable style={styles.valueText}>
                        {selectedOrder?.RequestDate}
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable style={styles.labelText}>
                        {translateFields(
                          "TotalOrdered",
                          t,
                          chinaTrackerTranslationMap,
                        )}
                      </Text>
                      <Text selectable style={styles.valueText}>
                        {selectedOrder?.TotalOrdered?.toLocaleString()} Pieces
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable style={styles.labelText}>
                        {translateFields(
                          "QtyToShip",
                          t,
                          chinaTrackerTranslationMap,
                        )}
                      </Text>
                      <Text selectable style={styles.valueText}>
                        {selectedOrder?.QtyToShip?.toLocaleString()} Pieces
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable style={styles.labelText}>
                        {translateFields(
                          "FulfillmentPercent",
                          t,
                          chinaTrackerTranslationMap,
                        )}
                      </Text>
                      <View style={styles.progressBar}>
                        <View
                          style={[styles.progressFilled, { flex: progressBar }]}
                        />
                        <View
                          style={[
                            styles.progressEmpty,
                            {
                              flex:
                                100 - (selectedOrder?.FulfillmentPercent ?? 0),
                            },
                          ]}
                        />
                      </View>
                    </View>
                  </View>
                </View>

                {/* Divider — desktop only */}
                {!isMobileView && <View style={styles.verticalLine} />}

                {/* ── Right: Tracking History ─────────────────────────── */}
                <View
                  style={[
                    styles.trackingContainer,
                    isMobileView && styles.trackingContainerMobile,
                  ]}
                >
                  {selectedOrder?.details?.map((detail: any, index: number) => (
                    <View key={index} style={styles.modalContainer}>
                      {/* Boolean fields */}
                      {[
                        {
                          key: "runOrder",
                          value: detail?.runOrder,
                          isText: true,
                        },
                        { key: "labelOrder", value: detail?.labelOrder },
                        {
                          key: "directShipment",
                          value: detail?.directShipment,
                        },
                        { key: "soakarPad", value: detail?.soakarPad },
                      ].map(({ key, value, isText }) => (
                        <View key={key} style={styles.detailRow2}>
                          <Text selectable style={styles.labelText}>
                            {translateFields(
                              key,
                              t,
                              chinaTrackerTranslationMap,
                            )}
                          </Text>
                          {isText ? (
                            <Text selectable style={styles.valueText}>
                              {value || ""}
                            </Text>
                          ) : (
                            <MaterialCommunityIcons
                              name={value === true ? "check" : "close"}
                              size={20}
                              color={value === true ? "green" : "red"}
                              style={styles.actionsIcons}
                            />
                          )}
                        </View>
                      ))}

                      {/* Numeric / text fields */}
                      <View style={styles.detailRow2}>
                        <Text selectable style={styles.labelText}>
                          {translateFields(
                            "PlannedQty",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable style={styles.valueText}>
                          {detail?.plannedQty?.toLocaleString() || ""}
                        </Text>
                      </View>
                      <View style={styles.detailRow2}>
                        <Text selectable style={styles.labelText}>
                          {translateFields(
                            "Duration",
                            t,
                            chinaTrackerTranslationMap,
                          )}
                        </Text>
                        <Text selectable style={styles.valueText}>
                          {detail?.duration || ""}
                        </Text>
                      </View>

                      {/* Date fields */}
                      {[
                        { key: "planStartDay", value: detail?.planStartDay },
                        {
                          key: "actualStartDay",
                          value: detail?.actualStartDay,
                        },
                        {
                          key: "ESTCompletionDate",
                          value: detail?.ESTCompletionDate,
                        },
                        {
                          key: "actualCompletionDate",
                          value: detail?.actualCompletionDate,
                        },
                        {
                          key: "shippingDeadline",
                          value: detail?.shippingDeadline,
                        },
                        { key: "ETD", value: detail?.ETD },
                        { key: "ETA", value: detail?.ETA },
                        {
                          key: "ExpectedArrival",
                          value: detail?.ExpectedArrival,
                        },
                      ].map(({ key, value }) => (
                        <View key={key} style={styles.detailRow2}>
                          <Text selectable style={styles.labelText}>
                            {translateFields(
                              key,
                              t,
                              chinaTrackerTranslationMap,
                            )}
                          </Text>
                          <Text selectable style={styles.valueText}>
                            {value ? formatLocalDate(value) : ""}
                          </Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            </ScrollView>
          )}
        </View>

        {/* ── Sub-modals ──────────────────────────────────────────────── */}
        {subModalOpen && trackerAction === "add" && (
          <TrackerEnquiryModal
            onClose={closeSubModal}
            visible={subModalOpen}
            order={selectedOrder}
          />
        )}
        {subModalOpen && trackerAction === "history" && (
          <TrackerHistoryModal
            onClose={closeSubModal}
            visible={subModalOpen}
            order={selectedOrder}
          />
        )}
        {subModalOpen && trackerAction === "flag" && (
          <ChinaTrackerFlagModal
            onClose={closeSubModal}
            visible={subModalOpen}
            order={selectedOrder}
          />
        )}
        {subModalOpen && trackerAction === "shipments" && (
          <ChinaShipmentsListModal
            onClose={closeSubModal}
            visible={subModalOpen}
            order={selectedOrder}
          />
        )}
      </View>
    </Modal>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getStatusColor = (status: string) => {
  switch (status) {
    case "On Time":
      return "green";
    case "At Risk":
      return "orange";
    case "Delayed":
      return "red";
    default:
      return "#000";
  }
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    width: "95%",
    maxWidth: 1100,
    maxHeight: "90%",
    backgroundColor: "#fff",
    borderRadius: 14,
    overflow: "hidden",
  },
  spinnerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  contentContainer: {
    padding: 10,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  contentContainerMobile: {
    flexDirection: "column",
  },
  orderDetails: {
    width: "40%",
    paddingRight: 10,
  },
  orderDetailsMobile: {
    width: "100%",
    paddingRight: 0,
  },
  detailBox: {
    backgroundColor: "#f2f2f2",
    borderRadius: 14,
    padding: 15,
    marginBottom: 10,
    minWidth: 200,
  },
  detailBoxMobile: {
    width: "100%",
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    backgroundColor: "#f2f2f2",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  detailRow2: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    backgroundColor: "#f2f2f2",
    alignItems: "flex-start",
    flexWrap: "wrap",
    paddingHorizontal: 5,
  },
  labelText: {
    fontWeight: "700",
    fontSize: 15,
    width: "30%",
  },
  valueText: {
    fontWeight: "400",
    fontSize: 13,
    textAlign: "right",
    flexWrap: "wrap",
    width: "65%",
  },
  progressBar: {
    width: 100,
    height: 10,
    flexDirection: "row",
    borderRadius: 5,
    overflow: "hidden",
  },
  progressFilled: {
    flex: 1,
    backgroundColor: "#56B7FD",
  },
  progressEmpty: {
    flex: 2,
    backgroundColor: "#e0e0e0",
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  trackingContainer: {
    width: "55%",
    paddingLeft: 10,
  },
  trackingContainerMobile: {
    width: "100%",
    paddingLeft: 0,
    marginTop: 10,
  },
  verticalLine: {
    width: 1,
    backgroundColor: "#e3e3e3",
    marginHorizontal: 10,
  },
  modalContainer: {
    marginTop: 10,
    backgroundColor: "#fff",
    padding: 20,
    width: "100%",
    elevation: 5,
    borderRadius: 10,
  },
  drawerHeaderStyle: {
    width: "100%",
    backgroundColor: "#fff",
    height: 50,
    borderBottomWidth: 1,
    borderColor: "#e3e3e3",
    justifyContent: "center",
  },
  actionsIcons: {
    paddingHorizontal: 4,
  },
});

export default ChinaTrackerDetailsReportModal;
